import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  alpha,
  Box,
  Divider,
  Stack,
  Typography,
  Button,
  Drawer,
  IconButton,
  Switch,
  TextField,
  ThemeProvider,
  darkTheme,
  useTheme,
} from 'omaha-design-system';
import { X } from '@phosphor-icons/react';
import { IRootState } from '../../../store/reducer';
import { leagueHasMinutes } from '../data/leagueConfig';

export interface DrawerFilterValues {
  minEdge: number;
  maxEV: number;
  minOdds: number;
  maxOdds: number;
  hideInjuredTeammate: boolean;
  requireBothModelsEV: boolean;
}

interface PropsFilterDrawerProps {
  open: boolean;
  onClose: () => void;
  onApply: (filters: DrawerFilterValues) => void;
}

const DEFAULTS: DrawerFilterValues = {
  minEdge: 0,
  maxEV: 0,
  minOdds: 0,
  maxOdds: 0,
  hideInjuredTeammate: false,
  requireBothModelsEV: false,
};

export const PropsFilterDrawer: React.FC<PropsFilterDrawerProps> = ({ open, onClose, onApply }) => {
  const theme = useTheme();
  const filters = useSelector((s: IRootState) => s.stokasticProps.filters);

  const inputSx = {
    '& .MuiInputBase-root': {
      bgcolor: alpha(theme.palette.common.white, 0.05),
      borderRadius: '4px',
      height: 40,
      '& input': {
        py: 1,
        px: 1.5,
        fontSize: '14px',
        '&::placeholder': {
          color: alpha(theme.palette.common.white, 0.4),
          opacity: 1,
        },
      },
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: alpha(theme.palette.common.white, 0.1),
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: alpha(theme.palette.common.white, 0.2),
    },
  };

  // Local state
  const [localMinEV, setLocalMinEV] = useState('');
  const [localMaxEV, setLocalMaxEV] = useState('');
  const [localMinOdds, setLocalMinOdds] = useState('');
  const [localMaxOdds, setLocalMaxOdds] = useState('');
  const [localHideInjuredTeammate, setLocalHideInjuredTeammate] = useState(false);
  const [localRequireBothModelsEV, setLocalRequireBothModelsEV] = useState(false);

  // Sync local state from Redux only when drawer opens
  const prevOpenRef = useRef(false);
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      setLocalMinEV(filters.minEdge ? String(filters.minEdge) : '');
      setLocalMaxEV(filters.maxEV ? String(filters.maxEV) : '');
      setLocalMinOdds(filters.minOdds ? String(filters.minOdds) : '');
      setLocalMaxOdds(filters.maxOdds ? String(filters.maxOdds) : '');
      setLocalHideInjuredTeammate(filters.hideInjuredTeammate);
      setLocalRequireBothModelsEV(filters.requireBothModelsEV);
    }
    prevOpenRef.current = open;
  }, [open, filters]);

  const handleApply = () => {
    onApply({
      minEdge: parseFloat(localMinEV) || 0,
      maxEV: parseFloat(localMaxEV) || 0,
      minOdds: parseFloat(localMinOdds) || 0,
      maxOdds: parseFloat(localMaxOdds) || 0,
      hideInjuredTeammate: localHideInjuredTeammate,
      requireBothModelsEV: localRequireBothModelsEV,
    });
    onClose();
  };

  const handleReset = () => {
    setLocalMinEV('');
    setLocalMaxEV('');
    setLocalMinOdds('');
    setLocalMaxOdds('');
    setLocalHideInjuredTeammate(DEFAULTS.hideInjuredTeammate);
    setLocalRequireBothModelsEV(DEFAULTS.requireBothModelsEV);
  };

  const hasChanges = useMemo(() => {
    const minEV = parseFloat(localMinEV) || 0;
    const maxEV = parseFloat(localMaxEV) || 0;
    const minOdds = parseFloat(localMinOdds) || 0;
    const maxOdds = parseFloat(localMaxOdds) || 0;
    return (
      minEV !== DEFAULTS.minEdge ||
      maxEV !== DEFAULTS.maxEV ||
      minOdds !== DEFAULTS.minOdds ||
      maxOdds !== DEFAULTS.maxOdds ||
      localHideInjuredTeammate !== DEFAULTS.hideInjuredTeammate ||
      localRequireBothModelsEV !== DEFAULTS.requireBothModelsEV
    );
  }, [localMinEV, localMaxEV, localMinOdds, localMaxOdds, localHideInjuredTeammate, localRequireBothModelsEV]);

  const drawerContent = (
    <Stack
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#272829',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.5,
          borderBottom: `1px solid ${alpha(theme.palette.common.white, 0.08)}`,
          flexShrink: 0,
          bgcolor: '#272829',
          position: 'sticky',
          top: 0,
          zIndex: 1,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '18px', fontFamily: 'Gilroy Black' }}>
          Filters
        </Typography>
        <IconButton
          onClick={onClose}
          aria-label="Close filter drawer"
          sx={{
            color: alpha(theme.palette.common.white, 0.7),
            '&:hover': {
              backgroundColor: alpha(theme.palette.common.white, 0.05),
            },
          }}
        >
          <X size={20} weight="regular" />
        </IconButton>
      </Box>

      {/* Scrollable Content */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          px: 2,
        }}
      >
        {/* EV Range % */}
        <Box sx={{ py: 2 }}>
          <Typography
            variant="body2"
            sx={{
              color: alpha(theme.palette.common.white, 0.7),
              mb: 1.5,
              fontWeight: 400,
            }}
          >
            EV Range %
          </Typography>
          <Stack direction="row" spacing={2}>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="caption"
                sx={{
                  color: alpha(theme.palette.common.white, 0.5),
                  display: 'block',
                  mb: 0.5,
                }}
              >
                Min
              </Typography>
              <TextField
                placeholder="Any"
                value={localMinEV}
                onChange={(e) => setLocalMinEV(e.target.value)}
                size="small"
                fullWidth
                variant="outlined"
                sx={inputSx}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="caption"
                sx={{
                  color: alpha(theme.palette.common.white, 0.5),
                  display: 'block',
                  mb: 0.5,
                }}
              >
                Max
              </Typography>
              <TextField
                placeholder="Any"
                value={localMaxEV}
                onChange={(e) => setLocalMaxEV(e.target.value)}
                size="small"
                fullWidth
                variant="outlined"
                sx={inputSx}
              />
            </Box>
          </Stack>
        </Box>

        <Divider sx={{ borderColor: alpha(theme.palette.common.white, 0.08) }} />

        {/* Odds Range */}
        <Box sx={{ py: 2 }}>
          <Typography
            variant="body2"
            sx={{
              color: alpha(theme.palette.common.white, 0.7),
              mb: 1.5,
              fontWeight: 400,
            }}
          >
            Odds Range
          </Typography>
          <Stack direction="row" spacing={2}>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="caption"
                sx={{
                  color: alpha(theme.palette.common.white, 0.5),
                  display: 'block',
                  mb: 0.5,
                }}
              >
                Min
              </Typography>
              <TextField
                placeholder="e.g. -200"
                value={localMinOdds}
                onChange={(e) => setLocalMinOdds(e.target.value)}
                size="small"
                fullWidth
                variant="outlined"
                sx={inputSx}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="caption"
                sx={{
                  color: alpha(theme.palette.common.white, 0.5),
                  display: 'block',
                  mb: 0.5,
                }}
              >
                Max
              </Typography>
              <TextField
                placeholder="e.g. +300"
                value={localMaxOdds}
                onChange={(e) => setLocalMaxOdds(e.target.value)}
                size="small"
                fullWidth
                variant="outlined"
                sx={inputSx}
              />
            </Box>
          </Stack>
        </Box>

        <Divider sx={{ borderColor: alpha(theme.palette.common.white, 0.08) }} />

        {/* Player Filters — the injured-teammate switch relies on projected minutes (NBA/NHL only) */}
        {leagueHasMinutes(filters.league) && (
          <Box sx={{ py: 2 }}>
            <Typography
              variant="body2"
              sx={{
                color: alpha(theme.palette.common.white, 0.7),
                mb: 1,
                fontWeight: 400,
              }}
            >
              Player Filters
            </Typography>

            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ py: 1 }}
            >
              <Box>
                <Typography variant="body2" sx={{ color: 'text.primary' }}>
                  Hide Questionable Impact
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: alpha(theme.palette.common.white, 0.4) }}
                >
                  Remove bets where a teammate is listed as questionable
                </Typography>
              </Box>
              <Switch
                checked={localHideInjuredTeammate}
                onChange={(e) => setLocalHideInjuredTeammate(e.target.checked)}
                size="small"
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: 'primary.main',
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: 'primary.main',
                  },
                }}
              />
            </Stack>
          </Box>
        )}

        <Divider sx={{ borderColor: alpha(theme.palette.common.white, 0.08) }} />

        {/* Model Filters */}
        <Box sx={{ py: 2 }}>
          <Typography
            variant="body2"
            sx={{
              color: alpha(theme.palette.common.white, 0.7),
              mb: 1,
              fontWeight: 400,
            }}
          >
            Model Filters
          </Typography>

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ py: 1 }}
          >
            <Box>
              <Typography variant="body2" sx={{ color: 'text.primary' }}>
                Both Models +EV
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: alpha(theme.palette.common.white, 0.4) }}
              >
                Only show bets where both Origination and Market projections are +EV
              </Typography>
            </Box>
            <Switch
              checked={localRequireBothModelsEV}
              onChange={(e) => setLocalRequireBothModelsEV(e.target.checked)}
              size="small"
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: 'primary.main',
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor: 'primary.main',
                },
              }}
            />
          </Stack>
        </Box>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.5,
          borderTop: `1px solid ${alpha(theme.palette.common.white, 0.08)}`,
          flexShrink: 0,
          bgcolor: '#272829',
          position: 'sticky',
          bottom: 0,
          zIndex: 1,
        }}
      >
        <Button
          variant="text"
          onClick={handleReset}
          disabled={!hasChanges}
          sx={{
            color: alpha(theme.palette.common.white, 0.7),
            fontWeight: 400,
            fontSize: '14px',
            textTransform: 'none',
            '&:hover': {
              backgroundColor: alpha(theme.palette.common.white, 0.05),
            },
            '&.Mui-disabled': {
              color: alpha(theme.palette.common.white, 0.3),
            },
          }}
        >
          Reset all
        </Button>
        <Button
          variant="contained"
          onClick={handleApply}
          sx={{
            bgcolor: 'primary.main',
            fontWeight: 500,
            fontSize: '14px',
            textTransform: 'none',
            px: 2.5,
            py: 0.75,
            borderRadius: '4px',
          }}
        >
          Apply Filters
        </Button>
      </Box>
    </Stack>
  );

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 320,
          bgcolor: '#272829',
        },
      }}
    >
      <ThemeProvider theme={darkTheme}>
        {drawerContent}
      </ThemeProvider>
    </Drawer>
  );
};
